import {
  CARTAZ_GRANDE,
  CARTAZ_MEDIO,
  CARTAZ_PEQUENO,
} from "../components/WorkSpace/CanvasViewer/EncarteConstants";

const APP_DOMAIN = process.env.REACT_APP_DOMAIN;
export const normalizeQuery = (q) => {
  q = q.replaceAll(" ", "");
  q = q.toLowerCase();
  return q;
};

const authenticateUrl = async (url, token, timeoutMs = 5000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(APP_DOMAIN + url, {
      headers: { Authorization: token },
      signal: controller.signal,
    });
    if (res.ok) {
      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      return objectUrl;
    } else {
      return null;
    }
  } catch (err) {
    console.log(err);
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};
/**
 *  Fetches an image via a CORS proxy and converts it to a data URL.
 * @param {*} link
 * @param {*} timeoutMs
 * @returns
 */
const CORStoDataURL = async (link, timeoutMs = 5000) => {
  const cors = process.env.REACT_APP_CORS_BASE_URL;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let url = "";
  try {
    const res = await fetch(cors + link, { signal: controller.signal });
    const buffer = await res.arrayBuffer();
    const blob = new Blob([buffer]);
    url = window.URL.createObjectURL(blob);
  } catch (err) {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
  return url;
};

export const toDataURL = async (link, token = "") => {
  if (!link || link === "") return "";
  if (link.includes("/resources/files"))
    return await authenticateUrl(link, token);
  else return await CORStoDataURL(link);
};

export const convertProductsImagesToDataURL = async (products, token) => {
  if (!products) return;
  // Download images in parallel so one slow/failing product can't delay the rest of the list.
  return await Promise.all(
    products.map(async (p) => {
      if (p.imageURL && p.imageURL !== "") {
        p.displayUrl = await toDataURL(p.imageURL, token);
      }
      return p;
    }),
  );
};

export const convertPriceStyleToDataURL = async (products, token) => {
  const arr = [];
  if (!products) return;
  for (const p of products) {
    let url = "";
    if (p.styles.priceStyle && p.styles.priceStyle !== "") {
      url = await toDataURL(p.styles.priceStyle, token);
      p.styles.priceStyleTmp = url;
    }
    arr.push(p);
  }
  return arr;
};

export const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
export const emailRegex = "^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,6}$";
export function validateField({
  name,
  value,
  required = false,
  minLength,
  maxLength,
  regex,
  customMessages = {},
}) {
  const messages = [];
  let isValid = true;

  if (required && (!value || value.length === 0)) {
    isValid = false;
    messages.push(`Campo \"${name}\" não pode estar vazio.`);
  }

  if (value && minLength !== undefined && value.length < minLength) {
    isValid = false;
    messages.push(customMessages.minLength);
  }

  if (value && maxLength !== undefined && value.length > maxLength) {
    isValid = false;
    messages.push(customMessages.maxLength);
  }

  if (value && regex && !new RegExp(regex).test(value)) {
    isValid = false;
    messages.push(customMessages.regex);
  }

  return { isValid, messages };
}

export const searchIntoCollection = (query, field, collection) => {
  if (!collection || !query) return [];
  const keywords = query.split(" ");
  const products = [];
  collection.forEach((p) => {
    keywords.forEach((key) => {
      if (key.length < 3) return;
      if (p.name.toLowerCase().includes(key.toLowerCase())) products.push(p);
    });
  });
  return products;
};

export const equalsIgnoreCase = (value1, value2) => {
  if (!value1 || !value2) return false;
  value1 = value1.toLowerCase();
  value2 = value2.toLowerCase();
  return value1 === value2;
};

export const copyProductPropertiesWithoutStyles = (copyFrom, copyTo) => {
  copyTo.name = copyFrom.name;
  copyTo.displayUrl = copyFrom.displayUrl;
  copyTo.imageURL = copyFrom.imageURL;
  copyTo.promotionPrice = copyFrom.promotionPrice;
  copyTo.promotionType = copyFrom.promotionType;
  copyTo.takeX = copyFrom.takeX;
  copyTo.payY = copyFrom.pay;
  copyTo.normalSellingPrice = copyFrom.normalSellingPrice;
  copyTo.measurement = copyFrom.measurement;
  return copyTo;
};

export const copyProductPropertiesWithStyles = (copyFrom, copyTo) => {
  copyTo = copyProductPropertiesWithoutStyles(copyFrom, copyTo);
  copyTo = copyStylesWithoutProperties(copyFrom, copyTo);
  return copyTo;
};

export const copyStylesWithoutProperties = (copyFrom, copyTo) => {
  copyTo.styles.priceStyle = copyFrom.styles.priceStyle;
  copyTo.styles.priceStyleTmp = copyFrom.styles.priceStyleTmp;
  copyTo.styles.bgColor = copyFrom.styles.bgColor;
  copyTo.styles.txtColor = copyFrom.styles.txtColor;
  copyTo.styles.priceBgColor = copyFrom.styles.priceBgColor;
  return copyTo;
};

export const scrollIntoStart = (ref) => {
  ref.current.scrollIntoView({ behavior: "smooth" });
};

export const isCartazFormat = (format) =>
  format === CARTAZ_PEQUENO ||
  format === CARTAZ_MEDIO ||
  format === CARTAZ_GRANDE;

/**
 * Redirects user to the path saved in sessionStorage as 'redirectAfterAuth',
 * or to a default path if not present.
 * @param {function} navigate - The navigate function from react-router.
 * @param {string} defaultPath - The fallback path (default: "/encartes").
 */
export function redirectAfterAuthOrDefault(
  navigate,
  defaultPath = "/encartes",
) {
  const redirectPath = sessionStorage.getItem("redirectAfterAuth");
  if (redirectPath) {
    sessionStorage.removeItem("redirectAfterAuth");
    navigate(redirectPath, { replace: true });
  } else {
    navigate(defaultPath, { replace: true });
  }
}

const PLAN_INFO = {
  MONTHLY_BASIC_PLAN: {
    label: "Plano mensal básico",
    price: "24,90",
  },
  MONTHLY_PREMIUM_PLAN: {
    label: "Plano mensal premium",
    price: "39,90",
  },
  TRIMESTER_PREMIUM_PLAN: {
    label: "Plano trimestral premium",
    price: "109,90",
  },
  SEMESTER_PREMIUM_PLAN: {
    label: "Plano semestral premium",
    price: "209,90",
  },
  YEARLY_PREMIUM_PLAN: {
    label: "Plano anual premium",
    price: "399,00",
  },
};

export function formatPlan(plan) {
  return PLAN_INFO[plan]?.label || plan;
}

export function formatPlanPrice(plan) {
  return PLAN_INFO[plan]?.price || "-";
}

export function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 *  Truncates a string to the specified maximum length, adding an ellipsis if truncated.
 * @param {*} str
 * @param {*} maxLength
 * @returns
 */

export function truncateWithEllipsis(str, maxLength) {
  if (!str) return "";
  return str.length > maxLength ? str.substring(0, maxLength) + "..." : str;
}

export function sanitizeLocation(location) {
  if (!location) return "";
  return location.endsWith("/") ? location.slice(0, -1) : location;
}

/**
 * This utility function check if a theme already had it's styles set (backwards compatibility, remove later)
 * @param {*} theme
 */
export function themeHasDefaultStyles(theme) {
  const defaultBgMainColor = "#f8a617";
  const defaultBgSecondaryColor = "#991B1B";
  const defaultMainTextColor = "#ffffff";
  const defaultSecondaryTextColor = "#ffffff";
  const defaultMainPriceTextColor = "#ffffff";
  const defaultSecondaryPriceTextColor = "#ffffff";

  const themeBgMainColor = theme.styles.bgMainColor;
  const themeBgSecondaryColor = theme.styles.bgSecondaryColor;
  const themeMainTextColor = theme.styles.mainTextColor;
  const themeSecondaryTextColor = theme.styles.secondaryTextColor;
  const themeMainPriceTextColor = theme.styles.mainPriceTextColor;
  const themeSecondaryPriceTextColor = theme.styles.secondaryPriceTextColor;
  if (
    equalsIgnoreCase(themeBgMainColor, defaultBgMainColor) &&
    equalsIgnoreCase(themeBgSecondaryColor, defaultBgSecondaryColor) &&
    equalsIgnoreCase(themeMainTextColor, defaultMainTextColor) &&
    equalsIgnoreCase(themeSecondaryTextColor, defaultSecondaryTextColor) &&
    equalsIgnoreCase(themeMainPriceTextColor, defaultMainPriceTextColor) &&
    equalsIgnoreCase(
      themeSecondaryPriceTextColor,
      defaultSecondaryPriceTextColor,
    )
  ) {
    return true;
  }
  return false;
}
